import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OAuthCallback() {
  const { platform } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("processing");
  const [message, setMessage] = useState("Processing authentication...");

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");

      if (error) {
        setStatus("error");
        setMessage(errorDescription || `Authentication failed: ${error}`);
        toast.error(`Failed to connect ${platform}`);
        setTimeout(() => navigate("/dashboard"), 3000);
        return;
      }

      if (!code || !state) {
        setStatus("error");
        setMessage("Invalid callback - missing authorization code or state");
        setTimeout(() => navigate("/dashboard"), 3000);
        return;
      }

      try {
        const response = await fetch(
          `${API}/oauth/callback/${platform}?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
          { credentials: "include" }
        );

        if (response.ok) {
          const data = await response.json();
          setStatus("success");
          setMessage(data.message || `Successfully connected to ${platform}!`);
          toast.success(`Connected to ${data.account_name || platform}`);
        } else {
          const errorData = await response.json();
          setStatus("error");
          setMessage(errorData.detail || "Connection failed");
          toast.error(errorData.detail || "Failed to connect");
        }
      } catch (error) {
        setStatus("error");
        setMessage("Network error - please try again");
        toast.error("Connection failed");
      }

      // Redirect back to dashboard after a short delay
      setTimeout(() => navigate("/dashboard"), 2000);
    };

    handleCallback();
  }, [platform, searchParams, navigate]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="text-center space-y-4">
        {status === "processing" && (
          <>
            <Loader2 className="w-12 h-12 text-violet-500 animate-spin mx-auto" />
            <h1 className="text-xl text-white font-semibold">Connecting to {platform}...</h1>
          </>
        )}
        
        {status === "success" && (
          <>
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto" />
            <h1 className="text-xl text-white font-semibold">Connected!</h1>
          </>
        )}
        
        {status === "error" && (
          <>
            <XCircle className="w-12 h-12 text-red-500 mx-auto" />
            <h1 className="text-xl text-white font-semibold">Connection Failed</h1>
          </>
        )}
        
        <p className="text-zinc-400">{message}</p>
        <p className="text-zinc-500 text-sm">Redirecting to dashboard...</p>
      </div>
    </div>
  );
}
